import request from "axios";
import { URL_CHANGE } from "header/ducks";
import { PAGINATION_PAGE_SIZE } from "common/variables";
import { getPaginationValues } from "common/utils";
import { logout } from "common/ducks/user";

export const TOGGLE_DRAWER = "notifications/TOGGLE_DRAWER";
export const FETCH_NOTIFICATIONS = "notifications/FETCH_NOTIFICATIONS";
export const FETCH_NOTIFICATIONS_REQUEST =
  "notifications/FETCH_NOTIFICATIONS_REQUEST";
export const FETCH_NOTIFICATIONS_SUCCESS =
  "notifications/FETCH_NOTIFICATIONS_SUCCESS";

export const READ_NOTIFICATION = "notifications/READ_NOTIFICATION";
export const READ_NOTIFICATION_REQUEST =
  "notifications/READ_NOTIFICATION_REQUEST";
export const READ_NOTIFICATION_SUCCESS =
  "notifications/READ_NOTIFICATION_SUCCESS";

export const UNREAD_NOTIFICATION = "notifications/UNREAD_NOTIFICATION";
export const UNREAD_NOTIFICATION_REQUEST =
  "notifications/UNREAD_NOTIFICATION_REQUEST";
export const UNREAD_NOTIFICATION_SUCCESS =
  "notifications/UNREAD_NOTIFICATION_SUCCESS";

export const SEE_NOTIFICATION = "notifications/SEE_NOTIFICATION";
export const SEE_NOTIFICATION_REQUEST =
  "notifications/SEE_NOTIFICATION_REQUEST";
export const SEE_NOTIFICATION_SUCCESS =
  "notifications/SEE_NOTIFICATION_SUCCESS";

export const DELETE_NOTIFICATION = "notifications/DELETE_NOTIFICATION";
export const DELETE_NOTIFICATION_REQUEST =
  "notifications/DELETE_NOTIFICATION_REQUEST";
export const DELETE_NOTIFICATION_SUCCESS =
  "notifications/DELETE_NOTIFICATION_SUCCESS";

const CLEAR_NOTIFICATION = "notifications/CLEAR_NOTIFICATION";
const CLEAR_NOTIFICATION_REQUEST = "notifications/CLEAR_NOTIFICATION_REQUEST";
const CLEAR_NOTIFICATION_SUCCESS = "notifications/CLEAR_NOTIFICATION_SUCCESS";

export const TOGGLE_SELECTED = "notifications/TOGGLE_SELECTED";
export const TOGGLE_ALL_SELECTED = "notifications/TOGGLE_ALL_SELECTED";

export function toggleDrawer() {
  return {
    type: TOGGLE_DRAWER,
  };
}

export function toggleSelected(notificationId) {
  return {
    type: TOGGLE_SELECTED,
    payload: notificationId,
  };
}

export function toggleAllSelected(notifications) {
  return {
    type: TOGGLE_ALL_SELECTED,
    payload: notifications.map(({ uuid }) => uuid),
  };
}

export const fetchNotifications = (userId, offset) => (dispatch) => {
  /* only paginate if offset is provided */
  const paginated = offset !== undefined;
  let url = `/api/v1/users/${userId}/notifications/`;
  if (paginated) {
    url += `?limit=${PAGINATION_PAGE_SIZE}&offset=${offset}`;
  }
  const opts = {
    method: "get",
    url,
  };
  const promise = request(opts);

  promise.catch((res) => {
    if (res.status === 403) {
      dispatch(logout(undefined, true));
      return;
    }
  });

  return dispatch({
    type: FETCH_NOTIFICATIONS,
    paginated,
    promise,
  });
};

const ActionMap = {
  read: READ_NOTIFICATION,
  unread: UNREAD_NOTIFICATION,
  see: SEE_NOTIFICATION,
  delete: DELETE_NOTIFICATION,
  clear: CLEAR_NOTIFICATION,
};

const ActionMethodMap = {
  read: "patch",
  unread: "patch",
  see: "patch",
  delete: "delete",
  clear: "patch",
};

export function notificationAction(action, userId, uuid) {
  const uuids = typeof uuid === "string" ? [uuid] : uuid;
  const opts = {
    method: ActionMethodMap[action],
    data: { uuids },
    url: `/api/v1/users/${userId}/notifications/${action}/`,
  };

  return {
    type: ActionMap[action],
    promise: request(opts),
    payload: { userId, uuids },
  };
}

export const INITIAL_STATE = {
  isLoading: false,
  data: {},
  showDrawer: false,
  actionType: null,
  selectedNotifications: [],
  pageCount: 1,
  currentPage: 1,
};

export function reduceNotification(state, action) {
  const { uuids } = action.payload;

  if (uuids && uuids.indexOf(state.uuid) === -1) {
    return state;
  }

  switch (action.type) {
    case READ_NOTIFICATION_REQUEST:
      return {
        ...state,
        read: true,
      };
    case UNREAD_NOTIFICATION_REQUEST:
      return {
        ...state,
        read: false,
      };
    case SEE_NOTIFICATION_REQUEST:
      return {
        ...state,
        seen: true,
      };
    case DELETE_NOTIFICATION_REQUEST:
      return null;
    case CLEAR_NOTIFICATION_REQUEST:
      return {
        ...state,
        cleared: true,
      };
    default:
      return state;
  }
}

export default function reducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case TOGGLE_DRAWER:
      return {
        ...state,
        showDrawer: !state.showDrawer,
      };
    case URL_CHANGE: {
      return {
        ...state,
        showDrawer: false,
      };
    }
    case FETCH_NOTIFICATIONS:
    case FETCH_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_NOTIFICATIONS_SUCCESS: {
      let updatedState = {
        ...state,
        isLoading: false,
        data: action.payload,
        currentPage: 1,
        pageCount: 1,
      };
      if (action.paginated) {
        const { currentPage, pageCount } = getPaginationValues(action.payload);
        updatedState = {
          ...updatedState,
          currentPage,
          pageCount,
        };
      }
      return updatedState;
    }

    case READ_NOTIFICATION_REQUEST:
    case UNREAD_NOTIFICATION_REQUEST:
    case SEE_NOTIFICATION_REQUEST:
    case DELETE_NOTIFICATION_REQUEST:
    case CLEAR_NOTIFICATION_REQUEST: {
      const reducedResults = (state.data.results || []).map((notification) =>
        reduceNotification(notification, action)
      );

      const data = {
        ...state.data,
        results: reducedResults.filter((result) => result !== null),
      };

      return {
        ...state,
        data,
      };
    }

    case READ_NOTIFICATION_SUCCESS:
    case UNREAD_NOTIFICATION_SUCCESS:
    case DELETE_NOTIFICATION_SUCCESS:
    case CLEAR_NOTIFICATION_SUCCESS:
      return {
        ...state,
        selectedNotifications: [],
      };
    case TOGGLE_SELECTED: {
      const listIndex = state.selectedNotifications.indexOf(action.payload);
      const addedNotification = [
        ...state.selectedNotifications,
        action.payload,
      ];
      const removedNotification = state.selectedNotifications
        .slice(0, listIndex)
        .concat(state.selectedNotifications.slice(listIndex + 1));

      return {
        ...state,
        selectedNotifications:
          listIndex === -1 ? addedNotification : removedNotification,
      };
    }
    case TOGGLE_ALL_SELECTED:
      return {
        ...state,
        selectedNotifications: state.selectedNotifications.length
          ? []
          : action.payload,
      };
    default:
      return state;
  }
}
